import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { getDocuments } from '../services/database';
import {
  AlertTriangle,
  Clock,
  CalendarClock,
  Barcode,
  Building2,
  Package,
  Calendar as CalendarIcon,
  FileText,
  DollarSign
} from 'lucide-react';
import { format, differenceInDays, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const PageContainer = styled.div`
  padding: 24px 20px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 12px 10px;
  }
`;

const Header = styled.div`
  position: relative;
  padding: 22px 24px;
  margin-bottom: 18px;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.06) 0%, rgba(245, 158, 11, 0.08) 100%);
  border: 1px solid rgba(245, 158, 11, 0.22);
  border-radius: 20px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.8) inset,
    0 6px 20px -8px rgba(245, 158, 11, 0.18);

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -10%;
    width: 220px;
    height: 220px;
    background: radial-gradient(circle, rgba(245, 158, 11, 0.2) 0%, transparent 65%);
    pointer-events: none;
  }

  h1 {
    position: relative;
    margin: 0;
    font-size: 2rem;
    font-weight: 800;
    letter-spacing: -1px;
    background: linear-gradient(135deg, #B91C1C 0%, #F59E0B 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    position: relative;
    margin: 4px 0 0;
    font-size: 13.5px;
    color: #64748b;
    font-weight: 500;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 16px 14px;
    border-radius: 16px;

    h1 {
      font-size: 1.4rem;
      letter-spacing: -0.5px;
    }
  }
`;

const StatCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 18px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }
`;

const StatCard = styled.div`
  position: relative;
  padding: 16px 18px;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 6px 18px -8px rgba(46, 91, 136, 0.12);
  transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 220ms ease;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${props => props.$accent};
  }

  &::after {
    content: '';
    position: absolute;
    top: -30%;
    right: -15%;
    width: 140px;
    height: 140px;
    background: ${props => props.$glow};
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-2px);
  }

  .icon-badge {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: ${props => props.$accent};
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;
    box-shadow: 0 4px 10px -2px rgba(0, 0, 0, 0.12);

    svg { width: 18px; height: 18px; stroke-width: 2.4; }
  }

  .number {
    font-size: 30px;
    font-weight: 800;
    letter-spacing: -1.2px;
    background: ${props => props.$gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-variant-numeric: tabular-nums;
    line-height: 1;
    margin-bottom: 4px;
  }

  .label {
    font-size: 11.5px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.7px;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 12px 10px;
    border-radius: 14px;

    .icon-badge {
      width: 28px;
      height: 28px;
      border-radius: 8px;
      margin-bottom: 6px;

      svg { width: 14px; height: 14px; }
    }

    .number { font-size: 22px; letter-spacing: -0.7px; }
    .label { font-size: 9.5px; letter-spacing: 0.3px; }
  }
`;

const FilterTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px;
  margin-bottom: 18px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(246, 250, 253, 0.92) 100%);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 14px;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 2px 8px -4px rgba(46, 91, 136, 0.06);
`;

const Tab = styled.button`
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  transition:
    background 180ms ease,
    color 180ms ease,
    box-shadow 180ms ease,
    transform 140ms cubic-bezier(0.34, 1.56, 0.64, 1);

  ${props => props.active ? `
    background: linear-gradient(135deg, #2E5B88 0%, #4FB3BF 100%);
    color: #ffffff;
    border: 1px solid transparent;
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.25) inset,
      0 4px 12px -3px rgba(46, 91, 136, 0.4);
  ` : `
    background: transparent;
    color: #475569;
    border: 1px solid transparent;

    &:hover {
      background: rgba(79, 179, 191, 0.08);
      color: #2E5B88;
    }
  `}

  &:active {
    transform: scale(0.97);
  }

  .count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    margin-left: 6px;
    background: ${props => props.active ? 'rgba(255, 255, 255, 0.25)' : 'rgba(148, 163, 184, 0.18)'};
    color: ${props => props.active ? '#ffffff' : '#64748b'};
    border-radius: 999px;
    font-size: 10.5px;
    font-weight: 700;
  }
`;

const AlertsGrid = styled.div`
  display: grid;
  gap: 12px;
`;

const AlertCard = styled.div`
  position: relative;
  padding: 18px 20px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdfe 100%);
  border: 1px solid ${props => props.$expired
    ? 'rgba(239, 68, 68, 0.3)'
    : 'rgba(245, 158, 11, 0.3)'};
  border-radius: 16px;
  overflow: hidden;
  isolation: isolate;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 6px 18px -8px ${props => props.$expired
      ? 'rgba(239, 68, 68, 0.18)'
      : 'rgba(245, 158, 11, 0.18)'};
  transition:
    transform 260ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 260ms ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.$expired
      ? 'linear-gradient(90deg, #EF4444 0%, #B91C1C 50%, #EF4444 100%)'
      : 'linear-gradient(90deg, #F59E0B 0%, #D97706 50%, #F59E0B 100%)'};
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 170px;
    height: 170px;
    background: ${props => props.$expired
      ? 'radial-gradient(circle at top right, rgba(239, 68, 68, 0.1) 0%, transparent 60%)'
      : 'radial-gradient(circle at top right, rgba(245, 158, 11, 0.1) 0%, transparent 60%)'};
    pointer-events: none;
    z-index: 0;
  }

  & > * {
    position: relative;
    z-index: 2;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.95) inset,
      0 14px 32px -10px ${props => props.$expired
        ? 'rgba(239, 68, 68, 0.25)'
        : 'rgba(245, 158, 11, 0.25)'};
  }
`;

const AlertHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 6px;
  margin-bottom: 14px;
  padding-bottom: 14px;
  border-bottom: 1px dashed rgba(148, 163, 184, 0.3);

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: -0.3px;
    line-height: 1.3;
    flex: 1;
    min-width: 0;
  }
`;

const AlertBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  background: ${props => props.$expired
    ? 'linear-gradient(135deg, #EF4444 0%, #B91C1C 100%)'
    : 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)'};
  color: #ffffff;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.25) inset,
    0 2px 8px -2px ${props => props.$expired
      ? 'rgba(239, 68, 68, 0.45)'
      : 'rgba(245, 158, 11, 0.45)'};
  flex-shrink: 0;

  svg { width: 12px; height: 12px; stroke-width: 2.6; }
`;

const ProductInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  .icon {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(46, 91, 136, 0.1) 0%, rgba(79, 179, 191, 0.14) 100%);
    color: #2E5B88;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg { width: 14px; height: 14px; stroke-width: 2.3; }
  }

  .content {
    flex: 1;
    min-width: 0;
  }

  label {
    display: block;
    font-size: 9.5px;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    margin-bottom: 1px;
  }

  span {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.2;
  }
`;

const EmptyState = styled.div`
  padding: 48px 20px;
  text-align: center;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(246, 250, 253, 0.92) 100%);
  border: 1px dashed rgba(148, 163, 184, 0.35);
  border-radius: 16px;
  color: #94a3b8;
  font-size: 14px;
  font-weight: 500;
`;

export const Alerts = () => {
  const { userId } = useAuth();
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProducts();
  }, [userId]);

  const loadProducts = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const productsData = await getDocuments(userId, 'products');
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const categorizeProducts = () => {
    const now = new Date();
    const expired = [];
    const expiringSoon = [];
    const expiringLater = [];

    products.forEach(product => {
      const expirationDate = parseISO(product.expirationDate);
      const daysUntilExpiration = differenceInDays(expirationDate, now);

      if (daysUntilExpiration < 0) {
        expired.push({ ...product, daysUntilExpiration: Math.abs(daysUntilExpiration) });
      } else if (daysUntilExpiration <= 30) {
        expiringSoon.push({ ...product, daysUntilExpiration });
      } else if (daysUntilExpiration <= 90) {
        expiringLater.push({ ...product, daysUntilExpiration });
      }
    });

    return { expired, expiringSoon, expiringLater };
  };

  const { expired, expiringSoon, expiringLater } = categorizeProducts();

  const getFilteredProducts = () => {
    switch (filter) {
      case 'expired': return expired;
      case 'expiring-soon': return expiringSoon;
      case 'expiring-later': return expiringLater;
      default: return [...expired, ...expiringSoon, ...expiringLater];
    }
  };

  const filteredProducts = getFilteredProducts();

  return (
    <PageContainer>
      <Header>
        <h1>Alertas de Vencimiento</h1>
        <p>Monitorea los productos próximos a vencer y los ya vencidos</p>
      </Header>

      <StatCards>
        <StatCard
          $accent="linear-gradient(135deg, #EF4444, #B91C1C)"
          $glow="radial-gradient(circle, rgba(239,68,68,0.12), transparent 65%)"
          $gradient="linear-gradient(135deg, #B91C1C, #EF4444)"
        >
          <div className="icon-badge"><AlertTriangle /></div>
          <div className="number">{expired.length}</div>
          <div className="label">Vencidos</div>
        </StatCard>

        <StatCard
          $accent="linear-gradient(135deg, #F59E0B, #D97706)"
          $glow="radial-gradient(circle, rgba(245,158,11,0.14), transparent 65%)"
          $gradient="linear-gradient(135deg, #B45309, #F59E0B)"
        >
          <div className="icon-badge"><Clock /></div>
          <div className="number">{expiringSoon.length}</div>
          <div className="label">Próx. 30 días</div>
        </StatCard>

        <StatCard
          $accent="linear-gradient(135deg, #60A5FA, #2563EB)"
          $glow="radial-gradient(circle, rgba(37,99,235,0.12), transparent 65%)"
          $gradient="linear-gradient(135deg, #1E40AF, #2563EB)"
        >
          <div className="icon-badge"><CalendarClock /></div>
          <div className="number">{expiringLater.length}</div>
          <div className="label">30 a 90 días</div>
        </StatCard>
      </StatCards>

      <FilterTabs>
        <Tab active={filter === 'all'} onClick={() => setFilter('all')}>
          Todos <span className="count">{expired.length + expiringSoon.length + expiringLater.length}</span>
        </Tab>
        <Tab active={filter === 'expired'} onClick={() => setFilter('expired')}>
          Vencidos <span className="count">{expired.length}</span>
        </Tab>
        <Tab active={filter === 'expiring-soon'} onClick={() => setFilter('expiring-soon')}>
          Próximos 30 días <span className="count">{expiringSoon.length}</span>
        </Tab>
        <Tab active={filter === 'expiring-later'} onClick={() => setFilter('expiring-later')}>
          30 a 90 días <span className="count">{expiringLater.length}</span>
        </Tab>
      </FilterTabs>

      {loading ? (
        <EmptyState>Cargando alertas...</EmptyState>
      ) : (
        <AlertsGrid>
          {filteredProducts.map(product => {
            const isExp = product.daysUntilExpiration !== undefined &&
              expired.some(p => p.id === product.id);

            return (
              <AlertCard key={product.id} $expired={isExp}>
                <AlertHeader>
                  <h3>{product.name}</h3>
                  <AlertBadge $expired={isExp}>
                    {isExp ? <AlertTriangle /> : <Clock />}
                    {isExp
                      ? `Vencido hace ${product.daysUntilExpiration}d`
                      : `Vence en ${product.daysUntilExpiration}d`}
                  </AlertBadge>
                </AlertHeader>

                <ProductInfo>
                  <InfoItem>
                    <div className="icon"><Barcode /></div>
                    <div className="content">
                      <label>Código</label>
                      <span>{product.barcode || '—'}</span>
                    </div>
                  </InfoItem>

                  <InfoItem>
                    <div className="icon"><Building2 /></div>
                    <div className="content">
                      <label>Laboratorio</label>
                      <span>{product.laboratory || '—'}</span>
                    </div>
                  </InfoItem>

                  <InfoItem>
                    <div className="icon"><Package /></div>
                    <div className="content">
                      <label>Stock</label>
                      <span>{product.stock} unidades</span>
                    </div>
                  </InfoItem>

                  <InfoItem>
                    <div className="icon"><CalendarIcon /></div>
                    <div className="content">
                      <label>Vence</label>
                      <span>{format(parseISO(product.expirationDate), 'dd/MM/yyyy', { locale: es })}</span>
                    </div>
                  </InfoItem>

                  <InfoItem>
                    <div className="icon"><FileText /></div>
                    <div className="content">
                      <label>Factura</label>
                      <span>{product.invoiceNumber || '—'}</span>
                    </div>
                  </InfoItem>

                  <InfoItem>
                    <div className="icon"><DollarSign /></div>
                    <div className="content">
                      <label>Valor en stock</label>
                      <span>${(product.salePrice * product.stock).toFixed(2)}</span>
                    </div>
                  </InfoItem>
                </ProductInfo>
              </AlertCard>
            );
          })}

          {filteredProducts.length === 0 && (
            <EmptyState>No hay productos en esta categoría</EmptyState>
          )}
        </AlertsGrid>
      )}
    </PageContainer>
  );
};
